import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";

import "../styles/marketing.css";

import Seo, { ORGANIZATION_JSONLD, SOFTWARE_JSONLD } from "../components/marketing/Seo";
import MarketingNav from "../components/marketing/MarketingNav";
import MarketingFooter from "../components/marketing/MarketingFooter";
import HomeHero from "../components/marketing/home/HomeHero";
import ReplacesStrip from "../components/marketing/home/ReplacesStrip";
import ShowcaseSections from "../components/marketing/home/ShowcaseSections";
import WorkflowTimeline from "../components/marketing/home/WorkflowTimeline";
import CompareTable from "../components/marketing/home/CompareTable";
import TrustStrip from "../components/marketing/home/TrustStrip";
import PricingPreview from "../components/marketing/home/PricingPreview";
import HomeFaq, { HOME_FAQS } from "../components/marketing/home/HomeFaq";
import FinalCta from "../components/marketing/home/FinalCta";
import { loadMetaPixel } from "../utils/metaPixel";

import BookDemoModal from "./BookDemoModal";
import { Link } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : "/api";

const FAQ_JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: HOME_FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const MobileStickyCta = () => (
  <div className="mk-mobile-cta items-center gap-3" data-testid="mobile-sticky-cta">
    <div className="flex-1 min-w-0 pl-1.5">
      <div className="text-[12.5px] font-bold text-slate-900">Free 7-day trial</div>
      <div className="text-[11px] text-slate-500">No credit card required</div>
    </div>
    <Link to="/register" className="mk-btn mk-btn-primary mk-btn-sm shrink-0" data-testid="mobile-sticky-cta-btn">
      Start Free Trial
    </Link>
  </div>
);

const LandingPage = () => {
  const [plans, setPlans] = useState([]);
  const [discounts, setDiscounts] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [demoOpen, setDemoOpen] = useState(false);
  const [demoPlan, setDemoPlan] = useState(null);
  const { hash } = useLocation();

  const openDemoFor = (plan = null) => {
    setDemoPlan(plan);
    setDemoOpen(true);
  };

  useEffect(() => {
    loadMetaPixel();
  }, []);

  useEffect(() => {
    axios
      .get(`${API}/plans`)
      .then((res) => {
        setPlans((res.data.plans || []).filter((p) => p.is_active).sort((a, b) => a.price - b.price));
        setDiscounts(res.data.discounts || []);
      })
      .catch(() => {})
      .finally(() => setLoadingPlans(false));
  }, []);

  // Scroll to hash anchors (e.g. /#scheduling from nav dropdowns on subpages)
  useEffect(() => {
    if (!hash) return;
    const t = setTimeout(() => {
      const el = document.querySelector(hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 120);
    return () => clearTimeout(t);
  }, [hash]);

  const marketingText =
    discounts.length > 0 && discounts[0].marketing_text ? discounts[0].marketing_text : null;

  return (
    <div className="mk min-h-screen overflow-x-hidden" data-testid="landing-page-root">
      <Seo
        title="Fieldz AI | All-in-One Field Service Management Software"
        description="Run scheduling, dispatch, CRM, business phone & SMS, estimates, invoices, payments, and financial reporting from one platform. Free 7-day trial — no credit card required."
        path="/"
        jsonLd={[ORGANIZATION_JSONLD, SOFTWARE_JSONLD, FAQ_JSONLD]}
      />
      <MarketingNav />

      <main>
        <HomeHero onBookDemo={() => openDemoFor(null)} />
        <ReplacesStrip />
        <ShowcaseSections />
        <WorkflowTimeline />
        <CompareTable />
        <TrustStrip />
        <PricingPreview plans={plans} discounts={discounts} loading={loadingPlans} onBookDemo={openDemoFor} />
        <HomeFaq />
        <FinalCta marketingText={marketingText} onBookDemo={() => openDemoFor(null)} />
      </main>

      <MarketingFooter />
      <MobileStickyCta />

      <BookDemoModal
        isOpen={demoOpen}
        onClose={() => {
          setDemoOpen(false);
          setDemoPlan(null);
        }}
        isDark={false}
        preselectedPlan={demoPlan}
      />
    </div>
  );
};

export default LandingPage;
